import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FormGroup } from './components/FormGroup';
import { extractFormGroupProps, isErrorFormGroup, lowerCaseFilter, useIds } from './utils';
import { InputOrTextarea } from './components/InputOrTextarea';
import { FieldItem } from './components/FieldItem';
import classNames from 'classnames';
import { OptionsList } from './components/OptionsList';

export const Combobox = (props, ref) => {
    const {
        onChange,
        onSelectOption,
        options,
        renderOptions,
        value,
        multiline = false,
        loading = false,
        reset = false,
        filtered = true,
        fieldRef,
        optionsListCustom,
        srDescription,
        autoComplete = 'off',
        onReset,
        name,
        label,
        helpInput,
        helpBlock,
        renderFieldWrapper = ({ children }) => children,
        ...restProps
    } = props;
    const autocompleteRef = useRef(null);
    useImperativeHandle(ref, () => autocompleteRef.current);

    const ids = useIds(name, 'list', 'id', 'helpInput', 'helpBlock');

    const [displayedLabel, setDisplayedLabel] = useState(value || '');
    const [optionIndex, setOptionIndex] = useState(-1);
    const [optionsListVisible, setOptionsListVisible] = useState(options.length > 0);
    const [autoCompleteFocus, setAutoCompleteFocus] = useState(autoComplete);
    const [optionsList, setOptionsList] = useState(options);
    const [isLoading, setIsLoading] = useState(loading);
    const [withReset, setWithReset] = useState(reset);

    const documentClickHandler = useCallback(() => {
      if (optionsListVisible) {
        setOptionsListVisible(false);
        setAutoCompleteFocus(autoComplete);
      }
    }, [optionsListVisible]);

    const filteredOptions = useMemo(() => {
        if (!value || !filtered) return optionsList;
        return options.filter((opt) => lowerCaseFilter(opt, value));
      }, [filtered, optionsList, value]);

    const resetHandler = useCallback(() => {
        setOptionIndex(-1);
        setDisplayedLabel('');
        if (autocompleteRef.current) autocompleteRef.current.focus();
        setOptionsList([]);
        onReset?.();
    }, [onReset]);

    const updateComponentFromKeyboard = useCallback(({focusAutocomplete, newOptionIndex, preventedEvent, showOptions, isEndList}) => {
        if (preventedEvent) preventedEvent.preventDefault();
        if (focusAutocomplete && autocompleteRef.current) autocompleteRef.current.focus();
        if (showOptions !== undefined) setOptionsListVisible(showOptions);

        if (newOptionIndex === -2) {
            setOptionIndex(-1);
            if (autocompleteRef.current) autocompleteRef.current.focus();
        } else if (newOptionIndex !== undefined) {
            if (optionsListCustom && isEndList) {
                setOptionIndex(filteredOptions.length);
            } else if (filteredOptions.length + 1 <= newOptionIndex) {
                setOptionIndex(filteredOptions.length);
            } else {
                setOptionIndex(newOptionIndex);
            }
        }
    }, [filteredOptions]);

    const keyDownHandler = useCallback((event) => {
        switch (event.key) {
            case 'ArrowUp':
                updateComponentFromKeyboard({
                    newOptionIndex: optionIndex !== 0 ? optionIndex - 1 : -2,
                    preventedEvent: event
                });
                break;
            case 'ArrowDown':
                updateComponentFromKeyboard({
                    newOptionIndex:
                    optionIndex !== filteredOptions.length - 1 ? optionIndex + 1 : optionIndex,
                    preventedEvent: event,
                    isEndList: optionIndex === filteredOptions.length - 1,
                    ...(!optionsListVisible && { showOptions: true })
                });
                break;
            case 'Escape':
                updateComponentFromKeyboard({
                    focusAutocomplete: true,
                    preventedEvent: event,
                    showOptions: false,
                });
                break;
            case 'Tab':
                updateComponentFromKeyboard({ showOptions: false });
                break;
            case 'Enter':
                if (filteredOptions[optionIndex]) onSelectOption(filteredOptions[optionIndex]);
                if (filteredOptions[optionIndex] !== undefined) {
                    setDisplayedLabel(filteredOptions[optionIndex]?.properties.label);
                } else if (optionsListCustom) {
                    setDisplayedLabel(optionsListCustom.label);
                }
                updateComponentFromKeyboard({
                    focusAutocomplete: true,
                    preventedEvent: event,
                    showOptions: false,
                    newOptionIndex: -2,
                });
                break;
            default:
                if (autocompleteRef.current) autocompleteRef.current.focus();
                setOptionIndex(-1);
                break;
        }
    }, [updateComponentFromKeyboard, optionIndex, filteredOptions, optionsListVisible, onSelectOption, optionsListCustom]);

    let timeoutRef;
    const changeHandler = useCallback((event) => {
        clearTimeout(timeoutRef);
        const newLabel = event.target.value;
        setDisplayedLabel(newLabel);
        if(props.buildUrl) {
            setIsLoading(true);
            setWithReset(false);
            timeoutRef = setTimeout(() => {
                fetch(props.buildUrl(newLabel))
                    .then(res => res.json())
                    .then(res => {
                        setOptionsList(res.features)
                        setOptionsListVisible(true);
                        setIsLoading(false);
                        setWithReset(true);
                    })
            }, 300);
        }
        setAutoCompleteFocus(autoComplete);
    },[filteredOptions, onChange]);

    const clickHandler = useCallback(() => {
        const isShow = !optionsListVisible;
        setOptionsListVisible(isShow);
        setOptionIndex(isShow ? -1 : 0);
        setAutoCompleteFocus(isShow ? 'off' : autoComplete);
    }, [optionsListVisible]);

    const selectHandler = useCallback((option) => {
        setDisplayedLabel(option.properties.label);
        setOptionsListVisible(false);
        if (autocompleteRef.current) autocompleteRef.current.focus();
            onSelectOption(option);
            setAutoCompleteFocus(autoComplete);
        },[onSelectOption]
    );

    // useEffect utile uniquement pour controler la valeur du composant depuis l'extérieur
    // La règle eslint désactivée est volontaire pour éviter la boucle infinie
    useEffect(() => {
        if (value !== displayedLabel) setDisplayedLabel(value);
    }, [value]);

    useEffect(() => {
        document.addEventListener('click', documentClickHandler, true);
        return () => {
            document.removeEventListener('click', documentClickHandler, true);
        };
    });

    const field = useMemo(
        () => renderFieldWrapper({
            children:
                <FieldItem className='-combobox' ref={fieldRef}>
                    {multiline &&
                        <div className='grow-wrap' data-replicated-value={displayedLabel}>
                            <InputOrTextarea
                                aria-autocomplete='list'
                                aria-expanded={optionsListVisible}
                                aria-owns={ids.list}
                                autoComplete={autoCompleteFocus}
                                aria-describedby={classNames({[ids.helpInput]: props.helpInput, [ids.helpBlock]: isErrorFormGroup(props) }) || undefined}
                                aria-invalid={isErrorFormGroup(props)}
                                id={ids.id}
                                multiline={multiline}
                                name={name}
                                onClick={clickHandler}
                                onChange={changeHandler}
                                onKeyDown={keyDownHandler}
                                ref={autocompleteRef}
                                role='combobox'
                                rows={1}
                                value={displayedLabel}
                                {...(restProps)}
                            />
                        </div>
                    }
                    {!multiline &&
                        <InputOrTextarea
                            aria-autocomplete='list'
                            aria-expanded={optionsListVisible}
                            aria-owns={ids.list}
                            autoComplete={autoCompleteFocus}
                            aria-describedby={classNames({[ids.helpInput]: props.helpInput, [ids.helpBlock]: isErrorFormGroup(props)}) || undefined}
                            aria-invalid={isErrorFormGroup(props)}
                            id={ids.id}
                            name={name}
                            onClick={clickHandler}
                            onChange={changeHandler}
                            onKeyDown={keyDownHandler}
                            ref={autocompleteRef}
                            role='combobox'
                            type='text'
                            value={displayedLabel}
                            {...(restProps)}
                        />
                    }
                    {isLoading &&
                        <div className='loading'>
                            <span className='loader-svg' aria-hidden='true' />
                            <span className='sr-only'>Chargement des résultats en cours : {srDescription}</span>
                        </div>
                    }
                    {withReset && displayedLabel &&
                        <button
                            className='reset'
                            type='button'
                            title={`Effacer le contenu ${srDescription}`}
                            aria-label={`Effacer le contenu ${srDescription}`}
                            onClick={resetHandler}
                        >
                            <i className='maificon maificon-close' aria-hidden='true' />
                        </button>
                    }
                    <OptionsList
                        optionIndex={optionIndex}
                        name={props.name}
                        onKeyDown={keyDownHandler}
                        onSelectOption={selectHandler}
                        options={filteredOptions}
                        renderOptions={renderOptions}
                        optionsListVisible={optionsListVisible}
                        optionsListCustom={optionsListCustom}
                        loading={loading}
                        srDescription={srDescription}
                    />
                </FieldItem>
        }),
        [
            renderFieldWrapper,
            fieldRef,
            multiline,
            displayedLabel,
            optionsListVisible,
            ids.list,
            ids.helpInput,
            ids.helpBlock,
            ids.id,
            autoCompleteFocus,
            props,
            name,
            clickHandler,
            changeHandler,
            keyDownHandler,
            loading,
            srDescription,
            reset,
            resetHandler,
            optionIndex,
            selectHandler,
            filteredOptions,
            renderOptions,
            optionsListCustom,
        ]
    );

    return <FormGroup {...extractFormGroupProps(props)}>{field}</FormGroup>
}